import { cloneElement, useId, useRef, useState, type ComponentProps, type PointerEvent, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
  Bot,
  Users,
  ChevronDown,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Info,
  Plus,
  Zap,
  ChevronRight,
  Search,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { Agent } from "@/hooks/use-agents";
import { useAgentCatalogRefreshOnOpen } from "@/hooks/agent-catalog-refresh-context";
import { AgentAboutDialog } from "@/components/agent-about-dialog";

interface AgentDropdownProps {
  agents: Agent[];
  currentAgentId: string | null;
  loading: boolean;
  error: string | null;
  selectionLocked?: boolean;
  /** When true the control is fully disabled (opening/menu/selection blocked)
   * and rendered with locked styling. Forms use this while saving. */
  disabled?: boolean;
  getToken?: () => Promise<string>;
  onSelect: (agentId: string) => void;
  /** Optional per-item action used by sidebar agent launchers. */
  onCreateSession?: (agentId: string) => void | Promise<void>;
  className?: string;
  /** Accessible name when a surrounding form omits its visible field label. */
  ariaLabel?: string;
  /** Stretch the trigger to fill its container and left-align the agent label.
   * Used in form dialogs; the default compact mode is for chat headers. */
  fullWidth?: boolean;
  /** Shorten the selected-label allowance before the header uses overflow. */
  triggerDensity?: "full" | "compact";
  /** Whether the current user is an ae10 admin (tenant + role both required).
   * When false, the custom agent ref input is hidden. */
  isAdminUser?: boolean;
  /**
   * Optional trigger for compact contextual launchers. The menu, keyboard
   * behavior, and selection policy remain owned by this component.
   */
  trigger?: ReactElement;
}

// ---------------------------------------------------------------------------
// AgentRef display helper
// ---------------------------------------------------------------------------

/**
 * If the agent ID carries a "server-slug/agent" prefix, format it to make the
 * server target visible at a glance.  Otherwise return the plain ID.
 *
 * "surgical-fusion/site_maintainer" → "site_maintainer @ surgical-fusion"
 */
function formatAgentRef(raw: string | null): { label: string; isCustomRef: boolean } {
  if (!raw) return { label: "", isCustomRef: false };
  const slashIdx = raw.indexOf("/");
  if (slashIdx > 0) {
    const slug = raw.slice(0, slashIdx);
    const agentId = raw.slice(slashIdx + 1);
    // Only format if the slug looks like a valid server slug
    if (/^[a-z0-9][a-z0-9-]*$/.test(slug) && agentId && !agentId.includes("/")) {
      return { label: `${agentId} @ ${slug}`, isCustomRef: true };
    }
  }
  return { label: raw, isCustomRef: false };
}

// ---------------------------------------------------------------------------
// Validation hint for the custom-ref input
// ---------------------------------------------------------------------------

/** True when the value is plausibly a "server-slug/agent" ref. */
function looksLikeRef(value: string): boolean {
  const slash = value.indexOf("/");
  if (slash <= 0) return false;
  const slug = value.slice(0, slash);
  const agent = value.slice(slash + 1);
  return /^[a-z0-9][a-z0-9-]*$/.test(slug) && agent.length > 0 && !agent.includes("/");
}

// ---------------------------------------------------------------------------
// Sorting helpers
// ---------------------------------------------------------------------------

function agentSortKey(a: Agent): number {
  return a.order ?? 100;
}

function sortAgents(list: Agent[]): Agent[] {
  return [...list].sort((a, b) => {
    const orderDiff = agentSortKey(a) - agentSortKey(b);
    if (orderDiff !== 0) return orderDiff;
    return a.name.localeCompare(b.name);
  });
}

type SearchMatchKind = "exact" | "prefix" | "wordBoundary" | "substring";

const directMatchScores: Record<SearchMatchKind, number> = {
  exact: 40,
  prefix: 30,
  wordBoundary: 20,
  substring: 10,
};

const descriptionMatchScores: Record<SearchMatchKind, number> = {
  exact: 4,
  prefix: 3,
  wordBoundary: 2,
  substring: 1,
};

interface RankedSearchMatch {
  agent: Agent;
  directTokenMatches: number;
  score: number;
  sourceIndex: number;
}

function getSearchMatchKind(value: string, searchTerm: string): SearchMatchKind | null {
  if (value === searchTerm) return "exact";

  let matchIndex = value.indexOf(searchTerm);
  if (matchIndex === -1) return null;
  if (matchIndex === 0) return "prefix";

  while (matchIndex !== -1) {
    if (!/[\p{L}\p{N}]/u.test(value[matchIndex - 1])) {
      return "wordBoundary";
    }
    matchIndex = value.indexOf(searchTerm, matchIndex + searchTerm.length);
  }

  return "substring";
}

function getSearchFieldScore(
  value: string,
  searchTerm: string,
  scores: Record<SearchMatchKind, number>
): number {
  const matchKind = getSearchMatchKind(value, searchTerm);
  return matchKind === null ? 0 : scores[matchKind];
}

/**
 * Every whitespace-delimited search term must match. Each term contributes
 * its best field score, while direct name/ID hits are counted separately so
 * they always rank ahead of results that match only in a description.
 */
function rankSearchMatches(items: Agent[], normalizedSearchQuery: string): Agent[] {
  const searchTerms = normalizedSearchQuery.split(/\s+/);
  const matches: RankedSearchMatch[] = [];

  for (const [sourceIndex, agent] of items.entries()) {
    const name = agent.name.toLocaleLowerCase();
    const id = agent.id.toLocaleLowerCase();
    const description = (agent.description ?? "").toLocaleLowerCase();
    let directTokenMatches = 0;
    let score = 0;
    let matchesEveryTerm = true;

    for (const searchTerm of searchTerms) {
      const directScore = Math.max(
        getSearchFieldScore(name, searchTerm, directMatchScores),
        getSearchFieldScore(id, searchTerm, directMatchScores)
      );
      const descriptionScore = getSearchFieldScore(
        description,
        searchTerm,
        descriptionMatchScores
      );

      if (directScore === 0 && descriptionScore === 0) {
        matchesEveryTerm = false;
        break;
      }

      if (directScore > 0) directTokenMatches += 1;
      score += directScore || descriptionScore;
    }

    if (matchesEveryTerm) {
      matches.push({ agent, directTokenMatches, score, sourceIndex });
    }
  }

  return matches
    .sort(
      (left, right) =>
        right.directTokenMatches - left.directTokenMatches ||
        right.score - left.score ||
        left.sourceIndex - right.sourceIndex
    )
    .map(({ agent }) => agent);
}

// ---------------------------------------------------------------------------
// Individual agent item renderer
// ---------------------------------------------------------------------------

// Radix menus move DOM focus on both pointer move (to the row) and leave
// (to the menu). Preserve an embedded input's focus until the user explicitly
// clicks or uses the keyboard to leave it. Keep this policy local to this picker.
function preserveInputFocus(event: PointerEvent<HTMLDivElement>) {
  const activeElement = event.currentTarget.ownerDocument.activeElement;
  if (
    activeElement instanceof HTMLInputElement
    && event.currentTarget.closest('[role="menu"]')?.contains(activeElement)
  ) {
    event.preventDefault();
  }
}

function AgentDropdownItem(props: ComponentProps<typeof DropdownMenuItem>) {
  return (
    <DropdownMenuItem
      onPointerMove={preserveInputFocus}
      onPointerLeave={preserveInputFocus}
      {...props}
    />
  );
}

function AgentConfigErrorWarning({
  agent,
  side = "top",
  className,
}: {
  agent: Agent | undefined;
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
}) {
  const { t } = useTranslation();
  if (!agent?.configError) return null;

  const label = t("agent.configError", { error: agent.configError });
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn("inline-flex shrink-0 text-amber-500", className)}
          aria-label={label}
          title={label}
        >
          <AlertTriangle className="size-3.5" aria-hidden="true" />
        </span>
      </TooltipTrigger>
      <TooltipContent side={side}>
        <p className="max-w-[320px] whitespace-pre-wrap text-xs">{label}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function AgentMenuItem({
  agent,
  currentAgentId,
  onSelect,
  onClose,
  onAbout,
  onCreateSession,
  locked,
  canShowAbout,
}: {
  agent: Agent;
  currentAgentId: string | null;
  onSelect: (agentId: string) => void;
  onClose: () => void;
  onAbout: (agentId: string) => void;
  onCreateSession?: (agentId: string) => void | Promise<void>;
  locked: boolean;
  /** Whether the About/Info lookup is available (requires a token provider). */
  canShowAbout: boolean;
}) {
  const { t } = useTranslation();
  const isTeam = agent.type === "team";
  const item = (
    <AgentDropdownItem
      key={agent.id}
      className={cn(
        "group flex items-center gap-2 text-xs",
        agent.id === currentAgentId && "bg-accent",
        locked && "cursor-not-allowed"
      )}
      onSelect={(event) => {
        if (locked) {
          event.preventDefault();
          return;
        }
        onSelect(agent.id);
        onClose();
      }}
    >
      {isTeam && (
        <Users className={cn("size-3.5 text-purple-500", locked && "opacity-50")} />
      )}
      <div className={cn("min-w-0 flex-1 flex flex-col", locked && "opacity-50")}>
        <span className="font-medium">{agent.name}</span>
        {agent.description && (
          <span className="text-[10px] text-muted-foreground line-clamp-1">
            {agent.description}
          </span>
        )}
      </div>
      {(agent.configError || onCreateSession || canShowAbout) && (
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <AgentConfigErrorWarning agent={agent} side="left" />
          {onCreateSession ? (
            <button
              type="button"
              aria-label={t("agent.newSessionWithAgent", { name: agent.name })}
              title={t("agent.newSessionWithAgent", { name: agent.name })}
              disabled={locked}
              className="inline-flex size-5 shrink-0 items-center justify-center rounded-sm text-muted-foreground/50 transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring group-hover:text-muted-foreground"
              onPointerDown={(event) => event.stopPropagation()}
              onPointerUp={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                if (locked) return;
                void onCreateSession(agent.id);
                onClose();
              }}
            >
              <Plus className="size-3.5" aria-hidden="true" />
            </button>
          ) : canShowAbout ? (
            <button
              type="button"
              aria-label={t("agent.aboutAgent", { name: agent.name })}
              title={t("agent.aboutAgent", { name: agent.name })}
              className="inline-flex size-5 shrink-0 items-center justify-center rounded-sm text-muted-foreground/50 transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring group-hover:text-muted-foreground"
              onPointerDown={(event) => event.stopPropagation()}
              onPointerUp={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                onAbout(agent.id);
              }}
            >
              <Info className="size-3.5" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      )}
    </AgentDropdownItem>
  );

  if (!locked) return item;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block cursor-not-allowed">{item}</span>
      </TooltipTrigger>
      <TooltipContent side="left">
        <p className="max-w-[240px] text-xs">{t("agent.lockedTooltip")}</p>
      </TooltipContent>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AgentDropdown({
  agents,
  currentAgentId,
  loading,
  error,
  selectionLocked: selectionLockedProp = false,
  disabled = false,
  getToken,
  onSelect,
  onCreateSession,
  className,
  ariaLabel,
  fullWidth = false,
  triggerDensity = "full",
  isAdminUser,
  trigger,
}: AgentDropdownProps) {
  // A fully disabled control behaves as a selection-locked one: the menu
  // stays uninspectable and no selection can be applied.
  const selectionLocked = selectionLockedProp || disabled;
  // The About lookup needs an access-token provider; render the affordance
  // only when one is supplied (e.g. the preferences dialog omits it).
  const canShowAbout = Boolean(getToken);
  const [open, setOpen] = useState(false);
  const [customRef, setCustomRef] = useState("");
  const [moreAgentsOpen, setMoreAgentsOpen] = useState(false);
  const [aboutAgentId, setAboutAgentId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const menuId = useId();
  const { t } = useTranslation();
  const refreshAgentCatalogOnOpen = useAgentCatalogRefreshOnOpen();

  const currentAgent = agents.find((a) => a.id === currentAgentId);
  const aboutAgent = agents.find((a) => a.id === aboutAgentId);
  const { label: refLabel, isCustomRef } = currentAgent
    ? { label: currentAgent.name || (currentAgentId ?? ""), isCustomRef: false }
    : formatAgentRef(currentAgentId);
  // Show translated "Select Agent…" placeholder when no agent is selected
  const displayLabel = refLabel || t("agent.selectAgent");
  const displayType = currentAgent?.type || "agent";

  const agentItems = sortAgents(agents.filter((a) => a.type === "agent"));
  const teamItems = sortAgents(agents.filter((a) => a.type === "team"));
  const primaryAgents = agentItems.filter((a) => a.tier !== "secondary");
  const secondaryAgents = agentItems.filter((a) => a.tier === "secondary");
  const hasSecondary = secondaryAgents.length > 0;
  const shouldShowSearch = agents.length > 7;
  const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase();
  const isSearching = shouldShowSearch && normalizedSearchQuery.length > 0;
  const matchingItems = isSearching
    ? [
        ...rankSearchMatches(agentItems, normalizedSearchQuery),
        ...rankSearchMatches(teamItems, normalizedSearchQuery),
      ]
    : [];

  function handleOpenChange(isOpen: boolean) {
    if (isOpen && !open) refreshAgentCatalogOnOpen?.();
    setOpen(isOpen);
    if (!isOpen) setSearchQuery("");
  }

  function openAgentAbout(agentId: string) {
    handleOpenChange(false);
    setAboutAgentId(agentId);
  }

  function applyCustomRef() {
    if (selectionLocked) return;
    const val = customRef.trim();
    if (!val) return;
    onSelect(val);
    setCustomRef("");
    handleOpenChange(false);
  }

  const triggerNode = trigger
    ? cloneElement(trigger, {
      "aria-expanded": open,
      "aria-controls": menuId,
      "aria-haspopup": "menu",
      "aria-busy": loading || undefined,
      // A caller-supplied button remains a normal menu trigger while a
      // selection is locked, matching the existing default control: users
      // can inspect the catalog but cannot apply a selection. Loading is the
      // only state that disables opening the controlled menu.
      disabled: loading || Boolean((trigger.props as { disabled?: boolean }).disabled),
      className: cn(
        (trigger.props as { className?: string }).className,
        selectionLocked && "cursor-not-allowed opacity-60",
      ),
    } as never)
    : (
      <Button
        variant="outline"
        size="sm"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={menuId}
        aria-haspopup="menu"
        aria-busy={loading || undefined}
        className={cn(
          "h-8 min-w-0 max-w-full gap-1.5 px-2.5 text-xs font-medium",
          fullWidth && "w-full justify-start",
          selectionLocked && "opacity-60 cursor-not-allowed"
        )}
        disabled={loading || disabled}
      >
        {loading ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : isCustomRef ? (
          <Zap className="size-3.5 text-amber-500" />
        ) : displayType === "team" ? (
          <Users className="size-3.5 text-purple-500" />
        ) : (
          <Bot className="size-3.5 text-blue-500" />
        )}
        <span
          className={cn(
            "min-w-0 truncate",
            fullWidth
              ? "flex-1 text-left"
              : triggerDensity === "compact"
                ? "max-w-[88px]"
                : "max-w-[140px]"
          )}
        >
          {displayLabel}
        </span>
        <AgentConfigErrorWarning agent={currentAgent} />
        <ChevronDown
          className={cn(
            "size-3 shrink-0 text-muted-foreground",
            fullWidth && "ml-auto"
          )}
        />
      </Button>
    );

  return (
    <>
      <TooltipProvider>
        <div className={cn("min-w-0", fullWidth && "w-full", className)}>
            <DropdownMenu open={open} onOpenChange={handleOpenChange}>
              <DropdownMenuTrigger asChild>
                {triggerNode}
              </DropdownMenuTrigger>

              <DropdownMenuContent
                id={menuId}
                align="end"
                className="w-72"
                onEscapeKeyDown={(event) => {
                  // Radix handles Escape at document capture, before the input
                  // handler. Clear search here first; otherwise close only this
                  // menu, allowing an enclosing dialog to handle the next Escape.
                  event.preventDefault();
                  if (document.activeElement === searchInputRef.current && searchQuery) {
                    setSearchQuery("");
                  } else {
                    if (document.activeElement === inputRef.current) setCustomRef("");
                    handleOpenChange(false);
                  }
                }}
                onOpenAutoFocus={(event) => {
                  if (shouldShowSearch) {
                    event.preventDefault();
                    searchInputRef.current?.focus();
                  }
                }}
                onKeyDownCapture={(event) => {
                  // Radix Menu typeahead treats every printable key from a
                  // descendant as a menu search. If it sees a key typed in
                  // this input, it can move focus to a menu item; a following
                  // Space then activates that item. Stop printable input keys
                  // before they reach Menu's bubble handler, but do not
                  // prevent their default so the browser still inserts them.
                  if (
                    event.target === searchInputRef.current
                    && event.key.length === 1
                    && !event.ctrlKey
                    && !event.altKey
                    && !event.metaKey
                  ) {
                    event.stopPropagation();
                  }
                }}
              >
                {shouldShowSearch && (
                  <>
                    <div className="px-2 pt-1.5 pb-1.5">
                      <div className="relative">
                        <Search
                          className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
                          aria-hidden="true"
                        />
                        <input
                          ref={searchInputRef}
                          type="text"
                          aria-label={t("agent.searchPlaceholder")}
                          className={cn(
                            "flex-1 h-7 rounded-md border bg-transparent px-2 text-xs",
                            "placeholder:text-muted-foreground",
                            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                            "w-full pl-7"
                          )}
                          placeholder={t("agent.searchPlaceholder")}
                          value={searchQuery}
                          onChange={(event) => setSearchQuery(event.target.value)}
                          onKeyDown={(event) => {
                            // Prevent the dropdown from closing on key events inside the input.
                            event.stopPropagation();
                            if (event.nativeEvent.isComposing) return;
                            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                              event.preventDefault();
                              const items = event.currentTarget.closest('[role="menu"]')
                                ?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([data-disabled])');
                              const item = event.key === "ArrowDown" ? items?.[0] : items?.[items.length - 1];
                              item?.focus();
                            }
                            if (event.key === "Enter") event.preventDefault();
                            if (event.key === "Enter" && isSearching && matchingItems.length > 0) {
                              if (selectionLocked) return;
                              onSelect(matchingItems[0].id);
                              handleOpenChange(false);
                            }
                          }}
                          // Keep focus inside the input when clicking it.
                          onClick={(event) => event.stopPropagation()}
                        />
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                  </>
                )}
                {currentAgentId !== null && !isSearching && (
                  <>
                    <AgentDropdownItem
                      className="flex items-center gap-2 text-xs"
                      onClick={() => {
                        openAgentAbout(currentAgentId);
                      }}
                    >
                      <Info className="size-3.5 text-muted-foreground" />
                      <span>{t("agent.aboutAgent", { name: displayLabel })}</span>
                    </AgentDropdownItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                {error ? (
                  <div className="flex items-center gap-2 px-2 py-2 text-xs text-destructive">
                    <AlertCircle className="size-4" />
                    <span>{error}</span>
                  </div>
                ) : (
                  <>
                    {isSearching ? (
                      matchingItems.length > 0 ? (
                        <DropdownMenuGroup>
                          {matchingItems.map((agent) => (
                            <AgentMenuItem
                              key={agent.id}
                              agent={agent}
                              currentAgentId={currentAgentId}
                              onSelect={onSelect}
                              onClose={() => handleOpenChange(false)}
                              onAbout={openAgentAbout}
                              onCreateSession={onCreateSession}
                              locked={selectionLocked}
                              canShowAbout={canShowAbout}
                            />
                          ))}
                        </DropdownMenuGroup>
                      ) : (
                        <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                          {t("agent.noSearchResults")}
                        </div>
                      )
                    ) : (
                      <>
                        {primaryAgents.length > 0 && (
                          <DropdownMenuGroup>
                            <DropdownMenuLabel className="text-xs text-muted-foreground">
                              {t("agent.agents")}
                            </DropdownMenuLabel>
                            {primaryAgents.map((agent) => (
                              <AgentMenuItem
                                key={agent.id}
                                agent={agent}
                                currentAgentId={currentAgentId}
                                onSelect={onSelect}
                                onClose={() => handleOpenChange(false)}
                                onAbout={openAgentAbout}
                                onCreateSession={onCreateSession}
                                locked={selectionLocked}
                              canShowAbout={canShowAbout}
                              />
                            ))}
                          </DropdownMenuGroup>
                        )}

                        {hasSecondary && (
                          <>
                            {primaryAgents.length > 0 && <DropdownMenuSeparator />}
                            <DropdownMenuGroup>
                              <AgentDropdownItem
                                className="flex w-full items-center justify-between text-xs font-medium text-muted-foreground"
                                onSelect={(e) => {
                                  e.preventDefault();
                                  setMoreAgentsOpen((v) => !v);
                                }}
                                aria-expanded={moreAgentsOpen}
                              >
                                <span>{t("agent.moreAgents")}</span>
                                <ChevronRight
                                  className={cn(
                                    "size-3.5 transition-transform",
                                    moreAgentsOpen && "rotate-90"
                                  )}
                                />
                              </AgentDropdownItem>
                              {moreAgentsOpen && (
                                <div className="pl-2 mt-0.5">
                                  {secondaryAgents.map((agent) => (
                                    <AgentMenuItem
                                      key={agent.id}
                                      agent={agent}
                                      currentAgentId={currentAgentId}
                                      onSelect={onSelect}
                                      onClose={() => handleOpenChange(false)}
                                      onAbout={openAgentAbout}
                                      onCreateSession={onCreateSession}
                                      locked={selectionLocked}
                              canShowAbout={canShowAbout}
                                    />
                                  ))}
                                </div>
                              )}
                            </DropdownMenuGroup>
                          </>
                        )}

                        {agentItems.length === 0 && teamItems.length > 0 && (
                          <DropdownMenuSeparator />
                        )}

                        {teamItems.length > 0 && (
                          <DropdownMenuGroup>
                            <DropdownMenuLabel className="text-xs text-muted-foreground">
                              {t("agent.teams")}
                            </DropdownMenuLabel>
                            {teamItems.map((team) => (
                              <AgentMenuItem
                                key={team.id}
                                agent={team}
                                currentAgentId={currentAgentId}
                                onSelect={onSelect}
                                onClose={() => handleOpenChange(false)}
                                onAbout={openAgentAbout}
                                onCreateSession={onCreateSession}
                                locked={selectionLocked}
                              canShowAbout={canShowAbout}
                              />
                            ))}
                          </DropdownMenuGroup>
                        )}

                        {agents.length === 0 && !loading && (
                          <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                            {t("common:errors.noAgents")}
                          </div>
                        )}
                      </>
                    )}

                    {/* --------------------------------------------------------
                        Custom agent ref — allows targeting agents on other
                        Agno OS instances via the "server-slug/agent-id" format.
                        e.g. "surgical-fusion/site_maintainer" routes to
                        http://surgical-fusion:8010 with agent site_maintainer.

                        Only shown to ae10 admins (both tenant + role required).
                    -------------------------------------------------------- */}
                    {isAdminUser && (
                      <>
                        <DropdownMenuSeparator />
                        <div
                          className={cn(
                            "px-2 pt-1.5 pb-0.5",
                            selectionLocked && "opacity-50 cursor-not-allowed"
                          )}
                          title={selectionLocked ? t("agent.lockedTooltip") : undefined}
                        >
                          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                            {t("agent.customRef")}
                          </p>
                          <div className="flex gap-1.5">
                            <input
                              ref={inputRef}
                              type="text"
                              className={cn(
                                "flex-1 h-7 rounded-md border bg-transparent px-2 text-xs",
                                "placeholder:text-muted-foreground",
                                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                                customRef && !looksLikeRef(customRef) && customRef.includes("/") && "border-destructive"
                              )}
                              placeholder={t("agent.customRefPlaceholder")}
                              value={customRef}
                              disabled={selectionLocked}
                              onChange={(e) => setCustomRef(e.target.value)}
                              onKeyDown={(e) => {
                                // Prevent the dropdown from closing on key events inside the input
                                e.stopPropagation();
                                if (e.key === "Enter") applyCustomRef();
                                if (e.key === "Escape") {
                                  setCustomRef("");
                                  handleOpenChange(false);
                                }
                              }}
                              // Keep focus inside the input when clicking it
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Button
                              size="sm"
                              variant="secondary"
                              className="h-7 px-2.5 text-xs shrink-0"
                              disabled={selectionLocked || !customRef.trim()}
                              onClick={(e) => {
                                e.stopPropagation();
                                applyCustomRef();
                              }}
                            >
                              {t("common:actions.apply")}
                            </Button>
                          </div>
                          {customRef && !looksLikeRef(customRef) && customRef.includes("/") && (
                            <p className="text-[10px] text-destructive mt-1">
                              {t("common:errors.slugInvalid")}
                            </p>
                          )}
                          {looksLikeRef(customRef) && (
                            <p className="text-[10px] text-muted-foreground mt-1">
                              {t("agent.routesTo", { slug: customRef.split("/")[0] })}
                            </p>
                          )}
                        </div>
                        <div className="h-2" />
                      </>
                    )}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
        </div>
      </TooltipProvider>
      {getToken && (
        <AgentAboutDialog
          agentId={aboutAgentId}
          agent={aboutAgent}
          open={aboutAgentId !== null}
          onOpenChange={(isOpen) => {
            if (!isOpen) setAboutAgentId(null);
          }}
          getToken={getToken}
        />
      )}
    </>
  );
}
